import { Link } from 'react-router-dom';

function CookiePolicy() {
  return (
    <div className="min-h-screen pb-8" data-testid="cookie-policy-page">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/" className="text-primary hover:underline mb-4 inline-block" data-testid="back-to-home-link">
          ← Back to Home
        </Link>

        <h1 className="text-3xl font-bold mb-6">Cookie Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last Updated: May 20, 2026</p>

        <div className="space-y-6 text-left">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p className="mb-4">
              This Cookie Policy explains how GameFeed.io, operated by Schuler Consultants, uses cookies,
              similar technologies (such as local storage), and third‑party analytics services on our website.
              It should be read alongside our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. What Are Cookies?</h2>
            <p className="mb-4">
              Cookies are small text files placed on your device when you visit a website. Some are required
              for a site to work; others are used to understand how visitors use the site so we can improve it.
              "Similar technologies" includes things like browser local storage and session storage, which can
              serve the same purposes as cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Our Approach</h2>
            <p className="mb-4">
              We try to keep what we collect to a minimum. We do not sell your data, and we do not run advertising
              cookies or cross‑site tracking. We do, however, use two analytics services so we can understand which
              articles people read and where the site is breaking. Those services are described below, and you can
              opt out of the non‑essential one at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Cookies and Similar Technologies We Use</h2>

            <h3 className="text-xl font-semibold mb-2 mt-4">4.1 Essential (always on)</h3>
            <p className="mb-4">
              These are required for the site to function and cannot be switched off:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Authentication cookie</strong> — keeps you signed in to your account.</li>
              <li><strong>Session cookie</strong> — maintains your session while you browse.</li>
              <li><strong>Theme preference (local storage)</strong> — remembers whether you've chosen light or dark mode.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">4.2 Analytics (optional, on by default outside the EU/UK; opt‑in in the EU/UK)</h3>
            <p className="mb-4">
              We use two analytics services. You can disable the non‑essential one at any time using the controls
              described in section 6.
            </p>
            <p className="mb-4">
              <strong>Simple Analytics</strong> — a privacy‑first, cookieless analytics service. It does not set
              cookies, does not collect personal information, respects the "Do Not Track" browser setting, and
              produces aggregate, anonymous statistics only.
            </p>
            <p className="mb-4">
              <strong>PostHog</strong> — a product analytics service that helps us understand how features are used
              and identify bugs. PostHog sets the following on your device:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>A first‑party cookie named <code>ph_phc_..._posthog</code> that stores an anonymous device identifier and session identifier.</li>
              <li>Equivalent entries in your browser's local storage and session storage.</li>
            </ul>
            <p className="mb-4">Through PostHog we collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Pages viewed and the order they were viewed in</li>
              <li>Clicks and form interactions on our pages (this is called "autocapture")</li>
              <li>Browser, operating system, screen size, approximate location derived from IP, and referring URL</li>
              <li>Performance metrics (page load timings, Core Web Vitals)</li>
              <li>Optionally, anonymized session recordings of how the page was used — these mask form inputs and text content by default</li>
            </ul>
            <p className="mb-4">
              PostHog data is processed by PostHog, Inc. in the United States on our behalf under a data‑processing
              agreement. We do not use PostHog data for advertising and we do not share it with other third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Third‑Party Cookies on Linked Sites</h2>
            <p className="mb-4">
              When you click on an external article link, you are taken to a third‑party website (for example, the
              original publisher). Those sites set their own cookies under their own policies, which we do not control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Managing Your Cookies</h2>

            <h3 className="text-xl font-semibold mb-2 mt-4">Consent banner (EU / UK / EEA visitors)</h3>
            <p className="mb-4">
              If you visit from the EU, UK, or EEA, we show a consent banner on your first visit. PostHog and any
              optional analytics are not loaded until you accept. You can change your choice at any time from the
              "Cookie preferences" link in the site footer.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">Opting out anywhere</h3>
            <p className="mb-4">
              You can opt out of PostHog at any time at{' '}
              <Link to="/cookie-preferences" className="text-primary hover:underline" data-testid="cookie-preferences-link">
                /cookie-preferences
              </Link>{' '}
              (or via the footer link). Opting out clears the PostHog cookie and stops further collection in this browser.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">Browser controls</h3>
            <p className="mb-4">Most browsers also let you:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>View what cookies are stored and delete them individually</li>
              <li>Block third‑party cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when you close the browser</li>
            </ul>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Note</p>
              <p className="text-yellow-700 dark:text-yellow-300">
                If you block essential cookies, parts of GameFeed.io may not work — in particular, staying signed in to your account.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Do Not Track and Global Privacy Control</h2>
            <p className="mb-4">
              We honor the <strong>Global Privacy Control (GPC)</strong> signal. If your browser sends GPC, we treat
              that as an opt‑out of optional analytics, the same as clicking "Reject" in the consent banner.
            </p>
            <p className="mb-4">
              Simple Analytics independently honors the legacy "Do Not Track" header.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Updates to This Policy</h2>
            <p className="mb-4">
              We may update this Cookie Policy from time to time to reflect changes to the services we use or to the
              law. When we do, we will update the "Last Updated" date at the top of this page. Material changes will
              also be announced on the site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Contact</h2>
            <p className="mb-4">
              If you have questions about this policy or how we use cookies, contact us at:{' '}
              <a href="mailto:info@gamefeed.io" className="text-primary hover:underline" data-testid="contact-email-link">
                info@gamefeed.io
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
